// umlaut-check: skip — "VorschlaegeGruppe" ist ein Komponenten-/Dateiname
// (technischer Bezeichner), bleibt ASCII.

import React, {useState} from "react";
import {Card, Table} from "react-bootstrap";
import {PiCaretDown, PiCaretUp} from "react-icons/pi";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import LkwBalken from "./LkwBalken.jsx";
import VorschlagZeile from "./VorschlagZeile.jsx";
import {compareKw, formatDatum, formatKw} from "./format.js";

/**
 * Eine aufklappbare Bestellwoche (oder die Sondergruppe "Überfällig/sofort"
 * ganz oben, Auftrag AP9 Punkt 3): Kopf mit KW, Montag-Datum, Summe,
 * Paletten, Dringlichkeits-Zählchips und den LKW-Balken der betroffenen
 * Ankunftswochen; aufgeklappt die Artikeltabelle.
 */
export default function VorschlaegeGruppe({gruppe, lkwAuslastung, benutzer, canOrder, canArtikel, onAuffuellen, onZeileGeaendert, standardOffen = false}) {
    const [offen, setOffen] = useState(standardOffen);

    const ankunftWochen = [...(gruppe.ankunft_wochen || [])]
        .sort(compareKw)
        .map((kw) => (lkwAuslastung || []).find((w) => w.kw === kw))
        .filter(Boolean);

    // formatKw(...) liefert bereits "KW 39/26" - ein zusätzliches "KW "
    // davor erzeugte "KW KW 39/26" (Sichtprüfung 1, Befund 23).
    const titel = gruppe.label || formatKw(gruppe.kw);
    const montag = gruppe.montag ? formatDatum(gruppe.montag) : null;

    return (
        <Card className={`mb-2 shadow-sm ${gruppe.ist_dringend ? "border-danger" : ""}`}>
            <button
                type="button"
                className="btn btn-link text-decoration-none text-body d-flex align-items-center gap-3 w-100 text-start px-3 py-2"
                aria-expanded={offen}
                onClick={() => setOffen((o) => !o)}
            >
                <span className="fw-semibold">{titel}</span>
                {montag && <span className="small text-muted">Montag {montag}</span>}
                <span className="small text-muted">{gruppe.summe_dosen.toLocaleString("de-DE")} Dosen · {gruppe.summe_paletten.toLocaleString("de-DE")} Paletten</span>
                <span className="d-flex gap-1">
                    {gruppe.anzahl_je_dringlichkeit.ueberfaellig > 0 && <span className="badge text-bg-danger">{gruppe.anzahl_je_dringlichkeit.ueberfaellig} überfällig</span>}
                    {gruppe.anzahl_je_dringlichkeit.sofort > 0 && <span className="badge text-bg-warning">{gruppe.anzahl_je_dringlichkeit.sofort} sofort</span>}
                    {gruppe.anzahl_je_dringlichkeit.bald > 0 && <span className="badge text-bg-info">{gruppe.anzahl_je_dringlichkeit.bald} bald</span>}
                    {gruppe.anzahl_je_dringlichkeit.geplant > 0 && <span className="badge text-bg-secondary">{gruppe.anzahl_je_dringlichkeit.geplant} geplant</span>}
                </span>
                <span className="ms-auto">{offen ? <PiCaretUp aria-hidden="true" /> : <PiCaretDown aria-hidden="true" />}</span>
            </button>

            {offen && (
                <Card.Body className="pt-2">
                    {ankunftWochen.length > 0 && (
                        <div className="mb-3">
                            <div className="small text-muted mb-1">LKW-Auslastung der betroffenen Ankunftswochen</div>
                            {ankunftWochen.map((w) => (
                                <LkwBalken key={w.kw} woche={w} onAuffuellen={onAuffuellen} canOrder={canOrder} />
                            ))}
                        </div>
                    )}

                    <Table responsive size="sm" className="align-middle mb-0">
                        <thead>
                            <tr>
                                <th />
                                <th>
                                    <HeaderWithHelp help="Farbton mit Artikelnummer, ggf. verlinkt zum Artikel-Blatt.">Artikel</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help="Vorgeschlagene Bestellmenge in Stück.">Menge</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help="Vorgeschlagene Bestellmenge, umgerechnet auf volle Paletten.">Paletten</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help="Wie dringend die Bestellung ist: überfällig, sofort, bald oder geplant.">Dringlichkeit</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help="Kalenderwoche, in der die bestellte Ware ankommen würde.">Ankunft</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help="Warum genau diese Menge und dieser Termin vorgeschlagen werden.">Begründung</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help="Vergleichswert aus Oskars Bestellrechner (Excel) – Vergleichsmaßstab, nicht Vorlage.">Oskars Vergleichswert</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help="Offen, übernommen oder verworfen.">Status</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help="Wer den Vorschlag bearbeitet – hier zuweisbar.">Zuständig</HeaderWithHelp>
                                </th>
                                {canOrder && (
                                    <th>
                                        <HeaderWithHelp help="Übernehmen, verwerfen oder auf „offen“ zurücksetzen.">Aktionen</HeaderWithHelp>
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {gruppe.artikel.map((zeile) => (
                                <VorschlagZeile key={zeile.id} zeile={zeile} benutzer={benutzer} canOrder={canOrder} canArtikel={canArtikel} onGeaendert={onZeileGeaendert} />
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            )}
        </Card>
    );
}
